import { supabase, TABLES, handleSupabaseError, handleSupabaseSuccess } from '../lib/supabase'
import { getCurrentUser } from './authService'

// =============================================================================
// CACHE MANAGEMENT
// =============================================================================

const cache = {
  customers: new Map(),
  vehicles: new Map(),
  workEntries: new Map(),
  drivers: new Map(),
  services: new Map(),
  fuelEntries: new Map(),
  rentals: new Map(),
  lastFetch: {
    customers: 0,
    vehicles: 0,
    workEntries: 0,
    drivers: 0,
    services: 0,
    fuelEntries: 0,
    rentals: 0
  }
}

// =============================================================================
// FIELD MAPPING HELPERS
// =============================================================================

// Convert snake_case to camelCase for frontend
const snakeToCamel = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(snakeToCamel)
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase())
      result[camelKey] = snakeToCamel(obj[key])
      return result
    }, {})
  }
  return obj
}

// Convert camelCase to snake_case for database
const camelToSnake = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(camelToSnake)
  } else if (obj !== null && obj.constructor === Object) {
    const result = {}
    Object.keys(obj).forEach(key => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      let value = camelToSnake(obj[key])
      
      // Convert empty strings to null for numeric fields
      if (value === '' && isNumericField(snakeKey)) {
        value = null
      }
      
      // Convert empty strings to null for time fields
      if (value === '' && isTimeField(snakeKey)) {
        value = null
      }
      
      result[snakeKey] = value
    })
    return result
  }
  return obj
}

// Helper function to identify numeric fields
const isNumericField = (fieldName) => {
  const numericFields = [
    'current_km',
    'purchase_price',
    'year',
    'hours_worked',
    'km_covered',
    'rate_per_hour',
    'base_amount',
    'discount_amount',
    'amount',
    'initial_payment',
    'agreed_amount',
    'paid_amount',
    'remaining_amount',
    'tax_amount'
  ]
  return numericFields.includes(fieldName)
}

// Helper function to identify time fields
const isTimeField = (fieldName) => {
  const timeFields = [
    'start_time',
    'end_time'
  ]
  return timeFields.includes(fieldName)
}

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

const isCacheValid = (type) => {
  return Date.now() - cache.lastFetch[type] < CACHE_DURATION
}

const clearCache = (type) => {
  if (type) {
    cache[type].clear()
    cache.lastFetch[type] = 0
  } else {
    Object.keys(cache).forEach(key => {
      if (key !== 'lastFetch') {
        cache[key].clear()
      }
    })
    Object.keys(cache.lastFetch).forEach(key => {
      cache.lastFetch[key] = 0
    })
  }
}

// =============================================================================
// CUSTOMERS CRUD OPERATIONS
// =============================================================================

export const customerService = {
  // Get all customers with caching
  getAll: async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh && isCacheValid('customers') && cache.customers.size > 0) {
        console.log('Using cached customers data')
        return handleSupabaseSuccess(Array.from(cache.customers.values()))
      }

      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.customers.clear()
      data.forEach(customer => cache.customers.set(customer.id, customer))
      cache.lastFetch.customers = Date.now()
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get customer by ID with caching
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.customers.has(id)) {
        return handleSupabaseSuccess(cache.customers.get(id))
      }

      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.customers.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new customer with cache invalidation
  create: async (customerData) => {
    try {
      const currentUser = getCurrentUser()
      console.log('Creating customer with data:', customerData)
      console.log('Current user:', currentUser)
      
      // Prepare insert data - completely exclude created_by if no valid user
      let insertData = { ...customerData }
      
      // Only add created_by if we have a valid user with an ID
      if (currentUser && currentUser.id) {
        insertData.created_by = currentUser.id
        console.log('Adding created_by with user ID:', currentUser.id)
      } else {
        console.log('No valid user found, creating customer without created_by field')
      }
      
      console.log('Insert data:', insertData)
      
      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .insert([insertData])
        .select()
        .single()

      if (error) {
        console.error('Supabase error creating customer:', error)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        return handleSupabaseError(error)
      }
      
      console.log('Customer created successfully:', data)
      
      // Update cache
      cache.customers.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      console.error('Exception creating customer:', error)
      return handleSupabaseError(error)
    }
  },

  // Update customer with cache invalidation
  update: async (id, customerData) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .update(customerData)
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.customers.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete customer with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from(TABLES.CUSTOMERS)
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.customers.delete(id)
      
      return handleSupabaseSuccess({ message: 'Customer deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// VEHICLES CRUD OPERATIONS
// =============================================================================

export const vehicleService = {
  // Get all vehicles with caching
  getAll: async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh && isCacheValid('vehicles') && cache.vehicles.size > 0) {
        console.log('Using cached vehicles data')
        return handleSupabaseSuccess(Array.from(cache.vehicles.values()))
      }

      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.vehicles.clear()
      camelCaseData.forEach(vehicle => cache.vehicles.set(vehicle.id, vehicle))
      cache.lastFetch.vehicles = Date.now()
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get vehicle by ID with caching
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.vehicles.has(id)) {
        return handleSupabaseSuccess(cache.vehicles.get(id))
      }

      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.vehicles.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new vehicle with cache invalidation
  create: async (vehicleData) => {
    try {
      const currentUser = getCurrentUser()
      
      // Convert camelCase to snake_case for database
      let mappedData = camelToSnake(vehicleData)
      
      // Only add created_by if we have a valid user with an ID
      if (currentUser && currentUser.id) {
        mappedData.created_by = currentUser.id
        console.log('Adding created_by with user ID:', currentUser.id)
      } else {
        console.log('No valid user found, creating vehicle without created_by field')
      }

      console.log('Creating vehicle with data:', mappedData)

      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .insert([mappedData])
        .select('*')
        .single()

      if (error) {
        console.error('Supabase error creating vehicle:', error)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        return handleSupabaseError(error)
      }
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.vehicles.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      console.error('Exception creating vehicle:', error)
      return handleSupabaseError(error)
    }
  },

  // Update vehicle with cache invalidation
  update: async (id, vehicleData) => {
    try {
      // Convert camelCase to snake_case for database
      const mappedData = camelToSnake(vehicleData)

      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .update(mappedData)
        .eq('id', id)
        .select('*')
        .single()

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.vehicles.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete vehicle with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from(TABLES.VEHICLES)
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.vehicles.delete(id)
      
      return handleSupabaseSuccess({ message: 'Vehicle deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Assign vehicle to customer
  assignToCustomer: async (vehicleId, customerId) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .update({
          assigned_to: customerId,
          is_assigned: true,
          assigned_date: new Date().toISOString()
        })
        .eq('id', vehicleId)
        .select('*')
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.vehicles.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Unassign vehicle from customer
  unassignFromCustomer: async (vehicleId) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .update({
          assigned_to: null,
          is_assigned: false,
          assigned_date: null
        })
        .eq('id', vehicleId)
        .select('*')
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.vehicles.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// WORK ENTRIES CRUD OPERATIONS
// =============================================================================

export const workEntryService = {
  // Get all work entries with caching
  getAll: async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh && isCacheValid('workEntries') && cache.workEntries.size > 0) {
        console.log('Using cached work entries data')
        return handleSupabaseSuccess(Array.from(cache.workEntries.values()))
      }

      const { data, error } = await supabase
        .from(TABLES.WORK_ENTRIES)
        .select('*')
        .order('work_date', { ascending: false })

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.workEntries.clear()
      camelCaseData.forEach(entry => cache.workEntries.set(entry.id, entry))
      cache.lastFetch.workEntries = Date.now()
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get work entry by ID with caching
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.workEntries.has(id)) {
        return handleSupabaseSuccess(cache.workEntries.get(id))
      }

      const { data, error } = await supabase
        .from(TABLES.WORK_ENTRIES)
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.workEntries.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new work entry with cache invalidation
  create: async (workEntryData) => {
    try {
      const currentUser = getCurrentUser()
      
      // Convert camelCase to snake_case for database
      const mappedData = camelToSnake(workEntryData)
      
      // Calculate amounts from work items
      const workItems = mappedData.work_items || []
      const baseAmount = workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
      const discount = parseFloat(mappedData.discount_amount || 0)
      const initialPayment = parseFloat(mappedData.initial_payment || 0)
      
      const finalAmount = Math.max(0, baseAmount - discount)
      const taxAmount = parseFloat(mappedData.tax_amount || 0)
      const totalAmount = finalAmount + taxAmount
      const remainingAmount = Math.max(0, totalAmount - initialPayment) // Ensure non-negative

      const insertData = {
        ...mappedData,
        base_amount: baseAmount,
        amount: finalAmount,
        remaining_amount: remainingAmount,
        // Preserve tax_amount if provided by user
        tax_amount: mappedData.tax_amount || null,
        created_by: currentUser?.id
      }
      
      console.log('Inserting work entry data:', insertData)
      console.log('Tax amount being inserted:', insertData.tax_amount)
      console.log('Driver ID being inserted:', insertData.driver_id)
      console.log('Driver ID type in insert:', typeof insertData.driver_id)
      console.log('Driver ID is null?', insertData.driver_id === null)
      console.log('Driver ID is undefined?', insertData.driver_id === undefined)

      const { data, error } = await supabase
        .from(TABLES.WORK_ENTRIES)
        .insert([insertData])
        .select('*')
        .single()

      if (error) {
        console.error('Database insert error:', error)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        return handleSupabaseError(error)
      }
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.workEntries.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Update work entry with cache invalidation
  update: async (id, workEntryData) => {
    try {
      // Convert camelCase to snake_case for database
      const mappedData = camelToSnake(workEntryData)
      
      // Always get current data to ensure we have all required fields
      const current = await workEntryService.getById(id)
      if (!current.success) return current

      // Recalculate amounts from work items
      const workItems = mappedData.work_items || current.data.workItems || []
      const baseAmount = workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
      const discount = mappedData.discount_amount !== undefined ? mappedData.discount_amount : current.data.discountAmount
      const finalAmount = Math.max(0, baseAmount - parseFloat(discount || 0))
      const taxAmount = parseFloat(mappedData.tax_amount || current.data.taxAmount || 0)
      const totalAmount = finalAmount + taxAmount

      mappedData.base_amount = baseAmount
      mappedData.amount = finalAmount
      mappedData.remaining_amount = Math.max(0, totalAmount - (current.data.paidAmount || 0))
      
      // Preserve tax_amount if provided by user, otherwise keep existing
      if (mappedData.tax_amount !== undefined) {
        mappedData.tax_amount = mappedData.tax_amount
      } else {
        mappedData.tax_amount = current.data.taxAmount
      }

      const { data, error } = await supabase
        .from(TABLES.WORK_ENTRIES)
        .update(mappedData)
        .eq('id', id)
        .select('*')
        .single()

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.workEntries.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete work entry with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from(TABLES.WORK_ENTRIES)
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.workEntries.delete(id)
      
      return handleSupabaseSuccess({ message: 'Work entry deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get work entries by customer
  getByCustomer: async (customerId) => {
    try {
      const { data, error } = await supabase
        .from('work_entries_with_details')
        .select('*')
        .eq('customer_id', customerId)
        .order('work_date', { ascending: false })

      if (error) return handleSupabaseError(error)
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get work entries by vehicle
  getByVehicle: async (vehicleId) => {
    try {
      const { data, error } = await supabase
        .from('work_entries_with_details')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .order('work_date', { ascending: false })

      if (error) return handleSupabaseError(error)
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Update payment status
  updatePaymentStatus: async (id, paymentStatus) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.WORK_ENTRIES)
        .update({ payment_status: paymentStatus })
        .eq('id', id)
        .select('*')
        .single()

      if (error) return handleSupabaseError(error)
      
      // Convert snake_case to camelCase for frontend
      const camelCaseData = snakeToCamel(data)
      
      // Update cache
      cache.workEntries.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// PAYMENTS CRUD OPERATIONS
// =============================================================================

export const paymentService = {
  // Get all payments
  getAll: async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.PAYMENTS)
        .select('*')
        .order('payment_date', { ascending: false })

      if (error) return handleSupabaseError(error)
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new payment
  create: async (workEntryId, paymentData) => {
    try {
      const currentUser = getCurrentUser()
      const { data, error } = await supabase
        .from(TABLES.PAYMENTS)
        .insert([{
          work_entry_id: workEntryId,
          ...paymentData,
          created_by: currentUser?.id
        }])
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Invalidate work entries cache since payment affects work entry
      clearCache('workEntries')
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Update payment
  update: async (id, paymentData) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.PAYMENTS)
        .update(paymentData)
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Invalidate work entries cache
      clearCache('workEntries')
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete payment
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from(TABLES.PAYMENTS)
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Invalidate work entries cache
      clearCache('workEntries')
      
      return handleSupabaseSuccess({ message: 'Payment deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// PAYMENT OFFERS CRUD OPERATIONS
// =============================================================================

export const paymentOfferService = {
  // Get all payment offers
  getAll: async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.PAYMENT_OFFERS)
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new payment offer
  create: async (workEntryId, offerData) => {
    try {
      const currentUser = getCurrentUser()
      const { data, error } = await supabase
        .from(TABLES.PAYMENT_OFFERS)
        .insert([{
          work_entry_id: workEntryId,
          ...offerData,
          created_by: currentUser?.id
        }])
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Invalidate work entries cache
      clearCache('workEntries')
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Update payment offer
  update: async (id, offerData) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.PAYMENT_OFFERS)
        .update(offerData)
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Invalidate work entries cache
      clearCache('workEntries')
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete payment offer
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from(TABLES.PAYMENT_OFFERS)
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Invalidate work entries cache
      clearCache('workEntries')
      
      return handleSupabaseSuccess({ message: 'Payment offer deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// DRIVERS CRUD OPERATIONS
// =============================================================================

export const driverService = {
  // Get all drivers with caching
  getAll: async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh && isCacheValid('drivers') && cache.drivers.size > 0) {
        console.log('Using cached drivers data')
        return handleSupabaseSuccess(Array.from(cache.drivers.values()))
      }

      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.drivers.clear()
      data.forEach(driver => cache.drivers.set(driver.id, driver))
      cache.lastFetch.drivers = Date.now()
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get driver by ID with caching
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.drivers.has(id)) {
        return handleSupabaseSuccess(cache.drivers.get(id))
      }

      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.drivers.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new driver with cache invalidation
  create: async (driverData) => {
    try {
      const currentUser = getCurrentUser()
      
      // Prepare insert data - completely exclude created_by if no valid user
      let insertData = { ...driverData }
      
      // Only add created_by if we have a valid user with an ID
      if (currentUser && currentUser.id) {
        insertData.created_by = currentUser.id
        console.log('Adding created_by with user ID:', currentUser.id)
      } else {
        console.log('No valid user found, creating driver without created_by field')
      }
      
      console.log('Creating driver with data:', insertData)
      
      const { data, error } = await supabase
        .from('drivers')
        .insert([insertData])
        .select()
        .single()

      if (error) {
        console.error('Supabase error creating driver:', error)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        return handleSupabaseError(error)
      }
      
      console.log('Driver created successfully:', data)
      
      // Update cache
      cache.drivers.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      console.error('Exception creating driver:', error)
      return handleSupabaseError(error)
    }
  },

  // Update driver with cache invalidation
  update: async (id, driverData) => {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .update({
          ...driverData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.drivers.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete driver with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('drivers')
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.drivers.delete(id)
      
      return handleSupabaseSuccess({ message: 'Driver deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// SERVICE SERVICE
// =============================================================================

export const serviceService = {
  // Get all services with caching
  getAll: async (forceRefresh = false) => {
    try {
      // Check cache first
      if (!forceRefresh && isCacheValid('services') && cache.services.size > 0) {
        const cachedData = Array.from(cache.services.values())
        return handleSupabaseSuccess(cachedData)
      }

      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)

      // Update cache
      cache.services.clear()
      data.forEach(service => cache.services.set(service.id, service))
      cache.lastFetch.services = Date.now()

      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get service by ID
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.services.has(id)) {
        return handleSupabaseSuccess(cache.services.get(id))
      }

      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.services.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new service with cache invalidation
  create: async (serviceData) => {
    try {
      const currentUser = getCurrentUser()
      
      // Prepare insert data - completely exclude created_by if no valid user
      let insertData = { ...serviceData }
      
      // Only add created_by if we have a valid user with an ID
      if (currentUser && currentUser.id) {
        insertData.created_by = currentUser.id
        console.log('Adding created_by with user ID:', currentUser.id)
      } else {
        console.log('No valid user found, creating service without created_by field')
      }
      
      console.log('Creating service with data:', insertData)
      console.log('Current user:', currentUser)
      
      const { data, error } = await supabase
        .from('services')
        .insert([insertData])
        .select()
        .single()

      if (error) {
        console.error('Supabase error creating service:', error)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        return handleSupabaseError(error)
      }
      
      console.log('Service created successfully:', data)
      
      // Update cache
      cache.services.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      console.error('Exception creating service:', error)
      return handleSupabaseError(error)
    }
  },

  // Update service with cache invalidation
  update: async (id, serviceData) => {
    try {
      const { data, error } = await supabase
        .from('services')
        .update({
          ...serviceData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Update cache
      cache.services.set(data.id, data)
      
      return handleSupabaseSuccess(data)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete service with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.services.delete(id)
      
      return handleSupabaseSuccess({ message: 'Service deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// FUEL SERVICE
// =============================================================================

export const fuelService = {
  // Get all fuel entries with caching
  getAll: async (forceRefresh = false) => {
    try {
      if (!forceRefresh && isCacheValid('fuelEntries')) {
        const cachedData = Array.from(cache.fuelEntries.values())
        return handleSupabaseSuccess(cachedData)
      }

      const { data, error } = await supabase
        .from('fuel_entries_with_details')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)

      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(data)
      cache.fuelEntries.clear()
      camelCaseData.forEach(entry => cache.fuelEntries.set(entry.id, entry))
      cache.lastFetch.fuelEntries = Date.now()

      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get fuel entry by ID
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.fuelEntries.has(id)) {
        return handleSupabaseSuccess(cache.fuelEntries.get(id))
      }

      const { data, error } = await supabase
        .from('fuel_entries_with_details')
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)

      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(data)
      cache.fuelEntries.set(camelCaseData.id, camelCaseData)

      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new fuel entry
  create: async (fuelData) => {
    try {
      const insertData = camelToSnake(fuelData)
      
      const { data, error } = await supabase
        .from('fuel_entries')
        .insert([insertData])
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Fetch the complete data from the view to match the structure
      const { data: completeData, error: viewError } = await supabase
        .from('fuel_entries_with_details')
        .select('*')
        .eq('id', data.id)
        .single()

      if (viewError) return handleSupabaseError(viewError)
      
      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(completeData)
      cache.fuelEntries.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Update fuel entry with cache invalidation
  update: async (id, fuelData) => {
    try {
      const { data, error } = await supabase
        .from('fuel_entries')
        .update({
          ...camelToSnake(fuelData),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Fetch the complete data from the view to match the structure
      const { data: completeData, error: viewError } = await supabase
        .from('fuel_entries_with_details')
        .select('*')
        .eq('id', id)
        .single()

      if (viewError) return handleSupabaseError(viewError)
      
      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(completeData)
      cache.fuelEntries.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete fuel entry with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('fuel_entries')
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.fuelEntries.delete(id)
      
      return handleSupabaseSuccess({ message: 'Fuel entry deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// RENTAL SERVICE
// =============================================================================

export const rentalService = {
  // Get all rentals with caching
  getAll: async (forceRefresh = false) => {
    try {
      if (!forceRefresh && isCacheValid('rentals')) {
        const cachedData = Array.from(cache.rentals.values())
        return handleSupabaseSuccess(cachedData)
      }

      const { data, error } = await supabase
        .from('rentals_with_details')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) return handleSupabaseError(error)

      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(data)
      cache.rentals.clear()
      camelCaseData.forEach(rental => cache.rentals.set(rental.id, rental))
      cache.lastFetch.rentals = Date.now()

      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Get rental by ID
  getById: async (id) => {
    try {
      // Check cache first
      if (cache.rentals.has(id)) {
        return handleSupabaseSuccess(cache.rentals.get(id))
      }

      const { data, error } = await supabase
        .from('rentals_with_details')
        .select('*')
        .eq('id', id)
        .single()

      if (error) return handleSupabaseError(error)

      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(data)
      cache.rentals.set(camelCaseData.id, camelCaseData)

      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Create new rental
  create: async (rentalData) => {
    try {
      const insertData = camelToSnake(rentalData)
      
      const { data, error } = await supabase
        .from('rentals')
        .insert([insertData])
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Fetch the complete data from the view to match the structure
      const { data: completeData, error: viewError } = await supabase
        .from('rentals_with_details')
        .select('*')
        .eq('id', data.id)
        .single()

      if (viewError) return handleSupabaseError(viewError)
      
      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(completeData)
      cache.rentals.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Update rental with cache invalidation
  update: async (id, rentalData) => {
    try {
      const { data, error } = await supabase
        .from('rentals')
        .update({
          ...camelToSnake(rentalData),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) return handleSupabaseError(error)
      
      // Fetch the complete data from the view to match the structure
      const { data: completeData, error: viewError } = await supabase
        .from('rentals_with_details')
        .select('*')
        .eq('id', id)
        .single()

      if (viewError) return handleSupabaseError(viewError)
      
      // Convert to camelCase and update cache
      const camelCaseData = snakeToCamel(completeData)
      cache.rentals.set(camelCaseData.id, camelCaseData)
      
      return handleSupabaseSuccess(camelCaseData)
    } catch (error) {
      return handleSupabaseError(error)
    }
  },

  // Delete rental with cache invalidation
  delete: async (id) => {
    try {
      const { error } = await supabase
        .from('rentals')
        .delete()
        .eq('id', id)

      if (error) return handleSupabaseError(error)
      
      // Remove from cache
      cache.rentals.delete(id)
      
      return handleSupabaseSuccess({ message: 'Rental deleted successfully' })
    } catch (error) {
      return handleSupabaseError(error)
    }
  }
}

// =============================================================================
// CACHE UTILITIES
// =============================================================================

export const cacheUtils = {
  clearAll: () => clearCache(),
  clearCustomers: () => clearCache('customers'),
  clearVehicles: () => clearCache('vehicles'),
  clearWorkEntries: () => clearCache('workEntries'),
  clearDrivers: () => clearCache('drivers'),
  clearServices: () => clearCache('services'),
  clearFuelEntries: () => clearCache('fuelEntries'),
  clearRentals: () => clearCache('rentals'),
  getCacheStats: () => ({
    customers: cache.customers.size,
    vehicles: cache.vehicles.size,
    workEntries: cache.workEntries.size,
    drivers: cache.drivers.size,
    services: cache.services.size,
    fuelEntries: cache.fuelEntries.size,
    rentals: cache.rentals.size,
    lastFetch: cache.lastFetch
  })
}
